import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Label } from '@monksflow/monks-ui';
import { X } from 'lucide-react';

interface AddAccountTagsFieldProps {
  tags: string[];
  tagInput: string;
  onTagInputChange: (value: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  onRemoveTag: (tag: string) => void;
  disabled?: boolean;
}

export function AddAccountTagsField({
  tags,
  tagInput,
  onTagInputChange,
  onKeyDown,
  onBlur,
  onRemoveTag,
  disabled,
}: AddAccountTagsFieldProps) {
  const { t } = useTranslation('knowledge');

  return (
    <div className="space-y-1.5">
      <Label htmlFor="sheet-account-tags">{t('mediaConnectors.addAccountDialog.tags')}</Label>
      <div className="flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 focus-within:ring-1 focus-within:ring-ring">
        {tags.map((tag) => (
          <Badge
            key={tag}
            variant="ghost"
            roundness="full"
            className="gap-1 border-border-light px-2 py-0.5 text-xs"
          >
            {tag}
            <button
              type="button"
              aria-label={t('mediaConnectors.addAccountDialog.removeTag', { tag })}
              onClick={() => onRemoveTag(tag)}
              className="ml-0.5 rounded-full p-0.5 hover:bg-muted"
              disabled={disabled}
            >
              <X className="size-3" />
            </button>
          </Badge>
        ))}
        <input
          id="sheet-account-tags"
          type="text"
          placeholder={
            tags.length === 0 ? t('mediaConnectors.addAccountDialog.typeAndPressEnter') : ''
          }
          value={tagInput}
          onChange={(e) => onTagInputChange(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          disabled={disabled}
          className="min-w-[120px] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>
    </div>
  );
}

AddAccountTagsField.displayName = 'AddAccountTagsField';
